import React, { useState, useEffect, useCallback } from "react";
import { Filter, X, Calendar, SlidersHorizontal } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function getHeaders() {
  const token = localStorage.getItem("session_token") || document.cookie.replace(/(?:(?:^|.*;\s*)session_token\s*=\s*([^;]*).*$)|^.*$/, "$1");
  return { "Content-Type": "application/json", Authorization: `Bearer ${token}` };
}

const UPDATE_OPS = [
  { value: "gte_days", label: "Há mais de X dias" },
  { value: "lte_days", label: "Há menos de X dias" },
  { value: "eq_days", label: "Exatamente X dias" },
  { value: "neq_days", label: "Não é X dias" },
];

export default function DealFilterModal({ open, onOpenChange, onApply, tc500 }) {
  const [pipelines, setPipelines] = useState([]);
  const [stages, setStages] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filter state
  const [filterPipeline, setFilterPipeline] = useState("");
  const [filterStage, setFilterStage] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterResponsavel, setFilterResponsavel] = useState("");
  const [users, setUsers] = useState([]);
  const [customValues, setCustomValues] = useState({});
  const [updateOp, setUpdateOp] = useState("");
  const [updateDays, setUpdateDays] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [pRes, cfRes, uRes] = await Promise.all([
          fetch(`${API}/pipelines`, { headers: getHeaders() }),
          fetch(`${API}/custom-fields?module=negocios`, { headers: getHeaders() }),
          fetch(`${API}/users`, { headers: getHeaders() }),
        ]);
        if (pRes.ok) setPipelines(await pRes.json());
        if (cfRes.ok) setCustomFields(await cfRes.json());
        if (uRes.ok) {
          const uData = await uRes.json();
          setUsers(Array.isArray(uData) ? uData : uData.users || []);
        }
      } catch { /* ignore */ }
    })();
  }, [open]);

  const handlePipelineChange = async (v) => {
    setFilterPipeline(v);
    setFilterStage("");
    if (v) {
      try {
        const res = await fetch(`${API}/pipelines/${v}/stages`, { headers: getHeaders() });
        if (res.ok) setStages(await res.json());
      } catch { setStages([]); }
    } else {
      setStages([]);
    }
  };

  const setCustomValue = (fieldName, value) => {
    setCustomValues(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleApply = async () => {
    setLoading(true);
    try {
      const body = {};
      if (filterPipeline) body.pipeline_id = filterPipeline;
      if (filterStage) body.stage_id = filterStage;
      if (filterStatus) body.status = filterStatus;
      if (filterResponsavel) body.responsavel_id = filterResponsavel;

      // Custom field filters
      const custom_filters = [];
      Object.entries(customValues).forEach(([key, val]) => {
        if (val !== "" && val !== undefined && val !== null) {
          custom_filters.push({ field: key, op: "eq", value: val });
        }
      });
      if (custom_filters.length > 0) body.custom_filters = custom_filters;

      // Updated_at filter
      if (updateOp && updateDays) {
        body.updated_at_op = updateOp;
        body.updated_at_days = parseInt(updateDays);
      }

      const res = await fetch(`${API}/email-campaigns/contacts-by-deals`, {
        method: "POST", headers: getHeaders(), body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.deal_count} negócio(s), ${(data.contacts || []).length} contato(s) com e-mail`);
        onApply(data.contacts || []);
        onOpenChange(false);
      }
    } catch { toast.error("Erro ao filtrar"); }
    setLoading(false);
  };

  const handleClear = () => {
    setFilterPipeline("");
    setFilterStage("");
    setFilterStatus("");
    setFilterResponsavel("");
    setCustomValues({});
    setUpdateOp("");
    setUpdateDays("");
  };

  const activeCount = [filterPipeline, filterStage, filterStatus, filterResponsavel, updateOp].filter(Boolean).length
    + Object.values(customValues).filter(v => v !== "" && v !== undefined).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl !p-0 !gap-0 max-h-[80vh] flex flex-col" data-testid="deal-filter-modal">
        <div className="px-5 pt-5 pb-3 border-b border-slate-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <SlidersHorizontal className="w-4 h-4" style={{ color: tc500 }} />
              Filtrar por negócios
            </DialogTitle>
            <DialogDescription className="text-xs">Selecione os critérios para filtrar contatos vinculados a negócios.</DialogDescription>
          </DialogHeader>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Row 1: Pipeline + Estágio */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-slate-600">Pipeline</Label>
              <Select value={filterPipeline} onValueChange={handlePipelineChange}>
                <SelectTrigger className="h-9 mt-1 text-sm bg-white" data-testid="filter-pipeline"><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent>
                  {pipelines.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Estágio</Label>
              <Select value={filterStage} onValueChange={setFilterStage} disabled={!filterPipeline}>
                <SelectTrigger className="h-9 mt-1 text-sm bg-white" data-testid="filter-stage"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  {stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 2: Status + Responsável */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-medium text-slate-600">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="h-9 mt-1 text-sm bg-white" data-testid="filter-status"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="aberto">Aberto</SelectItem>
                  <SelectItem value="ganho">Ganho</SelectItem>
                  <SelectItem value="perdido">Perdido</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Responsável</Label>
              <Select value={filterResponsavel} onValueChange={setFilterResponsavel}>
                <SelectTrigger className="h-9 mt-1 text-sm bg-white"><SelectValue placeholder="Todos" /></SelectTrigger>
                <SelectContent>
                  {users.map(u => <SelectItem key={u.id || u.user_id} value={u.id || u.user_id}>{u.name || u.nome}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 3: Última atualização */}
          <div>
            <Label className="text-xs font-medium text-slate-600 flex items-center gap-1.5">
              <Calendar className="w-3 h-3" /> Última atualização
            </Label>
            <div className="grid grid-cols-2 gap-3 mt-1">
              <Select value={updateOp} onValueChange={setUpdateOp}>
                <SelectTrigger className="h-9 text-sm bg-white" data-testid="filter-update-op"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  {UPDATE_OPS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="number" min={0} value={updateDays} onChange={e => setUpdateDays(e.target.value)} placeholder="Dias" className="h-9 text-sm" disabled={!updateOp} data-testid="filter-update-days" />
            </div>
          </div>

          {/* Custom fields */}
          {customFields.length > 0 && (
            <div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2 border-t border-slate-100 pt-3">Campos personalizados</p>
              <div className="grid grid-cols-2 gap-3">
                {customFields.map(f => (
                  <div key={f.field_name}>
                    <Label className="text-xs font-medium text-slate-600">{f.field_label}</Label>
                    {f.field_type === "select" ? (
                      <Select value={customValues[f.field_name] || ""} onValueChange={v => setCustomValue(f.field_name, v)}>
                        <SelectTrigger className="h-9 mt-1 text-sm bg-white"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          {(f.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : f.field_type === "number" ? (
                      <Input type="number" value={customValues[f.field_name] || ""} onChange={e => setCustomValue(f.field_name, e.target.value)} className="h-9 mt-1 text-sm" />
                    ) : f.field_type === "date" ? (
                      <Input type="date" value={customValues[f.field_name] || ""} onChange={e => setCustomValue(f.field_name, e.target.value)} className="h-9 mt-1 text-sm" />
                    ) : f.field_type === "checkbox" ? (
                      <Select value={customValues[f.field_name] || ""} onValueChange={v => setCustomValue(f.field_name, v)}>
                        <SelectTrigger className="h-9 mt-1 text-sm bg-white"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">Sim</SelectItem>
                          <SelectItem value="false">Não</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input value={customValues[f.field_name] || ""} onChange={e => setCustomValue(f.field_name, e.target.value)} className="h-9 mt-1 text-sm" placeholder="Contém..." />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
          <button onClick={handleClear} className="text-xs text-slate-500 hover:text-slate-700 transition-colors flex items-center gap-1" data-testid="clear-deal-filter">
            <X className="w-3 h-3" /> Limpar filtros {activeCount > 0 && `(${activeCount})`}
          </button>
          <Button size="sm" onClick={handleApply} disabled={loading} className="h-8 gap-1.5 text-white hover:opacity-90" style={{ backgroundColor: tc500 }} data-testid="apply-deal-filter">
            {loading ? <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Filter className="w-3.5 h-3.5" />}
            Aplicar filtro
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
